// features/inventory/pages/LedgerPage.tsx
/**
 * Transaction Ledger Page — Complete audit trail with smart filters and insights.
 *
 * Route: /inventory/ledger
 *
 * Features:
 * - Full transaction history with type filter pills
 * - Search by product name or batch number
 * - Date range filter with calendar inputs
 * - Summary stats: total records, stock in, stock out, net change
 * - Expandable rows with full transaction details
 * - Responsive table with branch, user, and notes columns
 * - Pagination with configurable page size
 * - Quick navigation to product detail
 * - Export capability
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Pagination } from '@/components/ui/Pagination';
import { getStockTransactions, StockTransaction } from '@/features/stock/services/stockApi';
import {
    ArrowLeft,
    Search,
    ClipboardList,
    Package,
    X,
    RefreshCw,
    Clock,
    User,
    Filter,
    Calendar,
    ShoppingCart,
    Truck,
    RotateCcw,
    ArrowRightLeft,
    PackageX,
    ShieldCheck,
    TrendingUp,
    TrendingDown,
    ChevronDown,
    ChevronUp,
    ChefHat,
    FlaskConical,
    PackageCheck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 30;

const TYPE_CONFIG: Record<string, {
    icon: React.ElementType;
    color: string;
    bg: string;
    label: string;
}> = {
    purchase: {
        icon: Truck,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        label: 'Purchase',
    },
    sale: {
        icon: ShoppingCart,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        label: 'Sale',
    },
    adjustment: {
        icon: RotateCcw,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        label: 'Adjustment',
    },
    waste: {
        icon: PackageX,
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        label: 'Waste',
    },
    damage: {
        icon: PackageX,
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        label: 'Damage',
    },
    correction: {
        icon: RotateCcw,
        color: 'text-purple-600',
        bg: 'bg-purple-50',
        label: 'Correction',
    },
    transfer_in: {
        icon: ArrowRightLeft,
        color: 'text-cyan-600',
        bg: 'bg-cyan-50',
        label: 'Transfer In',
    },
    transfer_out: {
        icon: ArrowRightLeft,
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        label: 'Transfer Out',
    },
    found_in_count: {
        icon: ClipboardList,
        color: 'text-teal-600',
        bg: 'bg-teal-50',
        label: 'Found in Count',
    },
    reservation: {
        icon: ShieldCheck,
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        label: 'Reservation',
    },
    production_consumption: {
        icon: ChefHat,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        label: 'Kitchen Production',
    },
    bom_consumption: {
        icon: FlaskConical,
        color: 'text-purple-600',
        bg: 'bg-purple-50',
        label: 'BOM Usage',
    },
    return: {
        icon: RotateCcw,
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        label: 'Return',
    },
    initial: {
        icon: PackageCheck,
        color: 'text-teal-600',
        bg: 'bg-teal-50',
        label: 'Initial Stock',
    },
};

const TRANSACTION_TYPES = Object.keys(TYPE_CONFIG);

// ============================================================
// MAIN COMPONENT
// ============================================================

export function LedgerPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const user = useAuthStore((s) => s.user);

    // Filters (supports URL query params e.g. ?search=...&type=...)
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [showFilters, setShowFilters] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    // Query
    const {
        data,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: [
            'stock-transactions',
            user?.tenantId,
            search,
            typeFilter,
            page,
            pageSize,
            startDate,
            endDate,
        ],
        queryFn: () =>
            getStockTransactions(user!.tenantId, {
                search: search || undefined,
                transactionType: typeFilter || undefined,
                limit: pageSize,
                page,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
            }),
        enabled: !!user?.tenantId,
        placeholderData: (prev) => prev,
    });

    // Derived data
    const transactions: StockTransaction[] = data?.data || [];
    const totalCount: number = data?.meta?.totalCount || 0;
    const totalPages: number = Math.max(
        1,
        Math.ceil(totalCount / pageSize)
    );

    // Type stats
    const typeStats = useMemo(() => {
        const stats: Record<
            string,
            { count: number; totalQty: number; totalValue: number }
        > = {};
        transactions.forEach((txn) => {
            const type = txn.transactionType || 'other';
            if (!stats[type])
                stats[type] = {
                    count: 0,
                    totalQty: 0,
                    totalValue: 0,
                };
            stats[type].count++;
            stats[type].totalQty += Math.abs(
                parseFloat(txn.quantity || '0')
            );
            stats[type].totalValue +=
                Math.abs(parseFloat(txn.quantity || '0')) *
                parseFloat(txn.unitCost || '0');
        });
        return stats;
    }, [transactions]);

    // Summary stats
    const totalInflow = transactions
        .filter((t) => parseFloat(t.quantity || '0') > 0)
        .reduce(
            (s, t) => s + parseFloat(t.quantity || '0'),
            0
        );
    const totalOutflow = transactions
        .filter((t) => parseFloat(t.quantity || '0') < 0)
        .reduce(
            (s, t) =>
                s + Math.abs(parseFloat(t.quantity || '0')),
            0
        );

    // Helpers
    const formatNumber = (val: string): string => {
        const n = parseFloat(val || '0');
        return isNaN(n)
            ? '0'
            : n.toLocaleString(undefined, {
                maximumFractionDigits: 3,
            });
    };

    const formatNaira = (val: string | undefined): string => {
        if (!val) return '—';
        const n = parseFloat(val);
        return isNaN(n) ? '—' : `₦${n.toLocaleString()}`;
    };

    const hasActiveFilters =
        search || typeFilter || startDate || endDate;

    const clearFilters = useCallback(() => {
        setSearch('');
        setTypeFilter('');
        setStartDate('');
        setEndDate('');
        setPage(1);
    }, []);

    const handlePageChange = useCallback(
        (newPage: number) => {
            if (
                newPage < 1 ||
                newPage > totalPages ||
                newPage === page
            )
                return;
            setPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        [totalPages, page]
    );

    const handleToggleExpand = useCallback(
        (id: string) => {
            setExpandedRow((prev) =>
                prev === id ? null : id
            );
        },
        []
    );

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg shadow-indigo-500/25">
                                <ClipboardList className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Transaction Ledger
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {totalCount.toLocaleString()}{' '}
                                    records
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => refetch()}
                            disabled={isFetching}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                            type="button"
                        >
                            <RefreshCw
                                className={clsx(
                                    'w-4 h-4',
                                    isFetching && 'animate-spin'
                                )}
                            />{' '}
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* Quick Summary Cards */}
                {transactions.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-white rounded-xl border border-slate-200 p-4">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                                Total Records
                            </p>
                            <p className="text-2xl font-bold text-slate-900 mt-1">
                                {totalCount.toLocaleString()}
                            </p>
                        </div>
                        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4">
                            <p className="text-[10px] text-emerald-600 uppercase tracking-wider flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" />{' '}
                                Stock In
                            </p>
                            <p className="text-2xl font-bold text-emerald-700 mt-1">
                                {totalInflow.toLocaleString()}
                            </p>
                            <p className="text-[10px] text-emerald-500">
                                units
                            </p>
                        </div>
                        <div className="bg-rose-50 rounded-xl border border-rose-200 p-4">
                            <p className="text-[10px] text-rose-600 uppercase tracking-wider flex items-center gap-1">
                                <TrendingDown className="w-3 h-3" />{' '}
                                Stock Out
                            </p>
                            <p className="text-2xl font-bold text-rose-700 mt-1">
                                {totalOutflow.toLocaleString()}
                            </p>
                            <p className="text-[10px] text-rose-500">
                                units
                            </p>
                        </div>
                        <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4">
                            <p className="text-[10px] text-indigo-600 uppercase tracking-wider">
                                Net Change
                            </p>
                            <p
                                className={clsx(
                                    'text-2xl font-bold mt-1',
                                    totalInflow -
                                        totalOutflow >=
                                        0
                                        ? 'text-emerald-700'
                                        : 'text-rose-700'
                                )}
                            >
                                {(
                                    totalInflow -
                                    totalOutflow
                                ).toLocaleString()}
                            </p>
                            <p className="text-[10px] text-indigo-500">
                                units
                            </p>
                        </div>
                    </div>
                )}

                {/* Type Filter Pills */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => {
                            setTypeFilter('');
                            setPage(1);
                        }}
                        className={clsx(
                            'px-3 py-2 rounded-xl text-xs font-medium transition-all border',
                            !typeFilter
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                        )}
                        type="button"
                    >
                        All Types ({totalCount})
                    </button>
                    {TRANSACTION_TYPES.map((type) => {
                        const config = TYPE_CONFIG[type];
                        if (!config) return null;
                        const Icon = config.icon;
                        const stats = typeStats[type];
                        const isActive = typeFilter === type;
                        return (
                            <button
                                key={type}
                                onClick={() => {
                                    setTypeFilter(
                                        isActive
                                            ? ''
                                            : type
                                    );
                                    setPage(1);
                                }}
                                className={clsx(
                                    'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all border',
                                    isActive
                                        ? `${config.bg} ${config.color} border-current`
                                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                                )}
                                type="button"
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {config.label}
                                {stats && (
                                    <span className="opacity-60">
                                        ({stats.count})
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by product name or batch number..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(
                                        e.target.value
                                    );
                                    setPage(1);
                                }}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white transition-all text-sm"
                            />
                            {search && (
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setPage(1);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={() =>
                                setShowFilters(
                                    !showFilters
                                )
                            }
                            className={clsx(
                                'flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all border',
                                showFilters ||
                                    hasActiveFilters
                                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                    : 'bg-slate-50 text-slate-600 border-slate-200'
                            )}
                            type="button"
                        >
                            <Filter className="w-4 h-4" />{' '}
                            Date Range
                        </button>
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium px-2"
                                type="button"
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    height: 0,
                                }}
                                animate={{
                                    opacity: 1,
                                    height: 'auto',
                                }}
                                exit={{
                                    opacity: 0,
                                    height: 0,
                                }}
                                className="overflow-hidden border-t border-slate-100 mt-1.5"
                            >
                                <div className="p-3 flex items-center gap-3">
                                    <Calendar className="w-4 h-4 text-slate-400" />
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) =>
                                            setStartDate(
                                                e.target
                                                    .value
                                            )
                                        }
                                        className="px-3 py-1.5 border rounded-lg text-xs"
                                    />
                                    <span className="text-xs text-slate-400">
                                        to
                                    </span>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) =>
                                            setEndDate(
                                                e.target
                                                    .value
                                            )
                                        }
                                        className="px-3 py-1.5 border rounded-lg text-xs"
                                    />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Transactions Table */}
                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : isError ? (
                    <ErrorState
                        title="Failed to load transactions"
                        onRetry={() => refetch()}
                    />
                ) : transactions.length === 0 ? (
                    <EmptyState
                        icon={
                            <ClipboardList className="w-16 h-16" />
                        }
                        title={
                            hasActiveFilters
                                ? 'No matching transactions'
                                : 'No transactions yet'
                        }
                        description={
                            hasActiveFilters
                                ? 'Try adjusting your filters.'
                                : 'Transactions appear after stock adjustments, purchases, or sales.'
                        }
                        action={
                            hasActiveFilters
                                ? {
                                    label: 'Clear Filters',
                                    onClick:
                                        clearFilters,
                                }
                                : undefined
                        }
                    />
                ) : (
                    <Card className="overflow-hidden shadow-sm border border-slate-200">
                        <div className="px-6 py-3 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                            <p className="text-xs text-slate-500">
                                Showing{' '}
                                <span className="font-semibold">
                                    {(page - 1) *
                                        pageSize +
                                        1}
                                    -
                                    {Math.min(
                                        page * pageSize,
                                        totalCount
                                    )}
                                </span>{' '}
                                of{' '}
                                <span className="font-semibold">
                                    {totalCount.toLocaleString()}
                                </span>
                            </p>
                            {isFetching && (
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <Spinner size="xs" />{' '}
                                    Updating...
                                </div>
                            )}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-8" />
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            <Clock className="w-3 h-3 inline mr-1" />{' '}
                                            Date
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Product
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-28">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Quantity
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">
                                            Unit Cost
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">
                                            Total
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">
                                            <User className="w-3 h-3 inline mr-1" />{' '}
                                            By
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {transactions.map(
                                        (txn, idx) => {
                                            const config =
                                                TYPE_CONFIG[
                                                txn
                                                    .transactionType
                                                ] ||
                                                TYPE_CONFIG.adjustment;
                                            const Icon =
                                                config.icon;
                                            const qty = parseFloat(
                                                txn.quantity ||
                                                '0'
                                            );
                                            const isPositive =
                                                qty >= 0;
                                            const unitCost =
                                                parseFloat(
                                                    txn.unitCost ||
                                                    '0'
                                                );
                                            const totalValue =
                                                Math.abs(
                                                    qty
                                                ) *
                                                unitCost;
                                            const isExpanded =
                                                expandedRow ===
                                                txn.id;

                                            return (
                                                <motion.tr
                                                    key={
                                                        txn.id
                                                    }
                                                    initial={{
                                                        opacity: 0,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                    }}
                                                    transition={{
                                                        delay:
                                                            Math.min(
                                                                idx *
                                                                0.01,
                                                                0.3
                                                            ),
                                                    }}
                                                    className={clsx(
                                                        'hover:bg-slate-50 transition-colors group',
                                                        isExpanded &&
                                                        'bg-indigo-50/30'
                                                    )}
                                                >
                                                    <td className="px-4 py-3">
                                                        <button
                                                            onClick={() =>
                                                                handleToggleExpand(
                                                                    txn.id
                                                                )
                                                            }
                                                            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                                                            type="button"
                                                        >
                                                            {isExpanded ? (
                                                                <ChevronUp className="w-4 h-4" />
                                                            ) : (
                                                                <ChevronDown className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
                                                        <p>
                                                            {new Date(
                                                                txn.createdAt
                                                            ).toLocaleDateString(
                                                                'en-NG'
                                                            )}
                                                        </p>
                                                        <p className="text-xs text-slate-400">
                                                            {new Date(
                                                                txn.createdAt
                                                            ).toLocaleTimeString(
                                                                'en-NG',
                                                                {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                }
                                                            )}
                                                        </p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className={clsx(
                                                                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                                                                    config.bg
                                                                )}
                                                            >
                                                                <Package
                                                                    className={clsx(
                                                                        'w-4 h-4',
                                                                        config.color
                                                                    )}
                                                                />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <button
                                                                    onClick={() =>
                                                                        txn.productId &&
                                                                        navigate(
                                                                            `/inventory/levels/${txn.productId}`
                                                                        )
                                                                    }
                                                                    className="text-sm font-medium text-slate-900 truncate max-w-[200px] hover:text-emerald-600 transition-colors text-left"
                                                                >
                                                                    {txn.productName ||
                                                                        'Unknown'}
                                                                </button>
                                                                {txn.batchNumber && (
                                                                    <p className="text-[10px] text-slate-400 font-mono">
                                                                        Batch:{' '}
                                                                        {
                                                                            txn.batchNumber
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span
                                                            className={clsx(
                                                                'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold',
                                                                config.bg,
                                                                config.color
                                                            )}
                                                        >
                                                            <Icon className="w-3 h-3" />
                                                            {
                                                                config.label
                                                            }
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <span
                                                            className={clsx(
                                                                'text-sm font-bold tabular-nums',
                                                                isPositive
                                                                    ? 'text-emerald-600'
                                                                    : 'text-rose-600'
                                                            )}
                                                        >
                                                            {isPositive
                                                                ? '+'
                                                                : ''}
                                                            {formatNumber(
                                                                txn.quantity
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right hidden sm:table-cell text-sm text-slate-600 tabular-nums">
                                                        {formatNaira(
                                                            txn.unitCost
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right hidden sm:table-cell text-sm font-semibold text-slate-700 tabular-nums">
                                                        {totalValue >
                                                            0
                                                            ? formatNaira(
                                                                totalValue.toFixed(
                                                                    2
                                                                )
                                                            )
                                                            : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 hidden lg:table-cell">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                                                                <User className="w-3 h-3 text-slate-400" />
                                                            </div>
                                                            <span className="text-xs text-slate-500">
                                                                {txn.performerName ||
                                                                    'System'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            );
                                        }
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalCount}
                                pageSize={pageSize}
                                onPageChange={
                                    handlePageChange
                                }
                                onPageSizeChange={(
                                    size
                                ) => {
                                    setPageSize(size);
                                    setPage(1);
                                }}
                            />
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
}